/**
 * Which model the platform runs, and what it costs.
 *
 * A JMC decision about spend, not about wording - so it lives here rather than
 * on a prompt release, and takes effect for every clinic's next generation. The
 * API keys are not here and never will be: they are environment variables, and a
 * key in a form is a key in a screenshot.
 *
 * Prices are shown in dollars because that is how a provider quotes them, and
 * stored in micro-USD because that is how this application counts money.
 *
 * An image is priced by FOUR numbers, not one. The prompt it was given, the text
 * it emits alongside the picture, the image tokens themselves, and - for the
 * providers that still bill that way - a flat fee per picture. Only the flat fee
 * was visible here, while the rate that actually dominates the bill (image
 * output, $30/Mtok on the current model against $0.25 for input) could be set
 * once at creation and never corrected. All four are editable now, and the table
 * says what one picture works out at rather than leaving somebody to multiply
 * three rates in their head.
 */
import { useState } from 'react';
import { ApiError, del, patch, post } from '../api.ts';
import { Card, Checklist, Empty, Pill, Table, rowOpen } from '../components/ui.tsx';
import { InfoButton } from '../components/InfoButton.tsx';
import { ModalForm } from '../components/Modal.tsx';
import { Field } from '../components/EditPanel.tsx';
import { Select } from '../components/Select.tsx';

export interface AiModel {
  id: string;
  kind: 'text' | 'image';
  provider: string;
  model: string;
  label: string;
  notes: string;
  /** bigint over the wire, so strings. Micro-USD. */
  input_micro_per_mtok: string | null;
  output_micro_per_mtok: string | null;
  image_micro_each: string | null;
  /** Micro-USD per million IMAGE-output tokens. Wins over the flat fee. */
  image_output_micro_per_mtok: string | null;
  is_active: boolean;
}

export interface AiModelList {
  items: AiModel[];
  /** Whether the key each provider needs is actually set in the environment. */
  keys: { openai: boolean; gemini: boolean };
}

const KIND_LABEL = { text: '文章生成', image: '画像生成' } as const;

/**
 * The article and the picture these estimates are costed against.
 *
 * Stated as constants, and stated on the screen, because an estimate whose
 * assumptions are hidden is a number people quote back as a fact. These are the
 * shapes this product actually generates: a full article of about 4,000
 * characters, and one 1K illustration - 1,290 image tokens is what Google's card
 * quotes for that size, and the art direction is a template plus a section of
 * the article.
 */
const TYPICAL = {
  articleInputTokens: 8_000,
  articleOutputTokens: 4_000,
  imagePromptTokens: 1_600,
  imageTextOutputTokens: 100,
  imageOutputTokens: 1_290,
} as const;

const rate = (micro: string | null): number | null => (micro === null ? null : Number(micro));

/** Micro-USD to dollars, for reading. 150000 → $0.15 */
const dollars = (micro: string | null): string =>
  micro === null ? '—' : `$${(Number(micro) / 1_000_000).toFixed(4)}`;

/** A dollar figure for an amount already in micro-USD. */
const money = (micro: number): string =>
  micro >= 10_000 ? `$${(micro / 1_000_000).toFixed(3)}` : `$${(micro / 1_000_000).toFixed(4)}`;

/**
 * What one article, or one picture, works out at under these rates.
 *
 * The image branch mirrors what the runtime actually does (see provider.ts): the
 * per-token rates win when the image-output rate is set, and the flat fee is the
 * fallback for a provider that prices by the picture or reports no usage. An
 * estimate that used a different formula from the biller would be worse than
 * none - it would look like a check and agree with nothing.
 */
function estimate(model: AiModel): { micro: number; basis: string } | null {
  if (model.kind === 'text') {
    const input = rate(model.input_micro_per_mtok);
    const output = rate(model.output_micro_per_mtok);
    if (input === null || output === null) return null;
    return {
      micro:
        (TYPICAL.articleInputTokens * input) / 1_000_000 +
        (TYPICAL.articleOutputTokens * output) / 1_000_000,
      basis: `入力${TYPICAL.articleInputTokens.toLocaleString('ja-JP')} + 出力${TYPICAL.articleOutputTokens.toLocaleString('ja-JP')}トークン`,
    };
  }

  const imageOutput = rate(model.image_output_micro_per_mtok);
  if (imageOutput !== null) {
    const input = rate(model.input_micro_per_mtok) ?? 0;
    const output = rate(model.output_micro_per_mtok) ?? 0;
    return {
      micro:
        (TYPICAL.imagePromptTokens * input) / 1_000_000 +
        (TYPICAL.imageTextOutputTokens * output) / 1_000_000 +
        (TYPICAL.imageOutputTokens * imageOutput) / 1_000_000,
      basis: `1K画像・画像出力${TYPICAL.imageOutputTokens.toLocaleString('ja-JP')}トークン`,
    };
  }

  const each = rate(model.image_micro_each);
  return each === null ? null : { micro: each, basis: '1枚あたりの固定単価' };
}

export function ModelSettings({ data, onChanged }: { data: AiModelList; onChanged: () => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<AiModel | null>(null);
  const [removing, setRemoving] = useState<AiModel | null>(null);
  const blank = {
    kind: 'text',
    provider: 'gemini',
    model: '',
    label: '',
    notes: '',
    inputPrice: '',
    outputPrice: '',
    imageOutputPrice: '',
    imagePrice: '',
  };
  const [form, setForm] = useState(blank);
  /*
   * The edit dialog's own copy, filled from the row when it opens. Dollars in
   * the fields, micro-USD on the wire - the conversion happens once, on save.
   */
  const [editForm, setEditForm] = useState({
    label: '',
    notes: '',
    inputPrice: '',
    outputPrice: '',
    imageOutputPrice: '',
    imagePrice: '',
  });

  const asDollars = (micro: string | null): string =>
    micro === null ? '' : String(Number(micro) / 1_000_000);

  const openEdit = (model: AiModel) => {
    setEditForm({
      label: model.label,
      notes: model.notes,
      inputPrice: asDollars(model.input_micro_per_mtok),
      outputPrice: asDollars(model.output_micro_per_mtok),
      imageOutputPrice: asDollars(model.image_output_micro_per_mtok),
      imagePrice: asDollars(model.image_micro_each),
    });
    setEditing(model);
  };

  /** A provider whose key is unset answers with the stub, whatever is selected. */
  const usable = (provider: string) =>
    provider === 'stub' || data.keys[provider as 'openai' | 'gemini'] === true;

  const report = (err: unknown, fallback: string) =>
    setError(err instanceof ApiError ? [err.message, ...err.fields].join(' / ') : fallback);

  async function activate(model: AiModel) {
    setBusy(model.id);
    setError(null);
    try {
      await post(`/admin/ai-models/${model.id}/activate`, {});
      onChanged();
    } catch (err) {
      report(err, '切り替えられませんでした');
    } finally {
      setBusy(null);
    }
  }

  /** Dollars in the field, micro-USD on the wire. Blank stays absent. */
  const micro = (value: string): number | undefined =>
    value.trim() === '' ? undefined : Math.round(Number(value) * 1_000_000);

  /*
   * `step="any"` on every price, deliberately.
   *
   * These are stored in micro-USD, so a real rate can be $0.034443 - and a
   * `step="0.0001"` field calls that invalid. The browser then refuses to submit
   * the form and says nothing anybody sees: the save button simply does not
   * work, which is exactly how this screen behaved for the per-image price.
   */
  const priceFields = (
    kind: 'text' | 'image',
    values: {
      inputPrice: string;
      outputPrice: string;
      imageOutputPrice: string;
      imagePrice: string;
    },
    set: (next: Partial<typeof values>) => void,
    prefix: string,
  ) => (
    <>
      <Field
        label="入力トークン単価"
        htmlFor={`${prefix}-input`}
        hint="100万トークンあたりUSD。渡したプロンプトの分です。"
        required={kind === 'text'}
      >
        <input
          id={`${prefix}-input`}
          type="number"
          step="any"
          min="0"
          inputMode="decimal"
          value={values.inputPrice}
          onChange={(event) => set({ inputPrice: event.target.value })}
        />
      </Field>
      <Field
        label={kind === 'text' ? '出力トークン単価' : 'テキスト出力単価'}
        htmlFor={`${prefix}-output`}
        hint={
          kind === 'text'
            ? '100万トークンあたりUSD。生成された文章の分です。'
            : '100万トークンあたりUSD。画像と一緒に返るテキストの分です。'
        }
        required={kind === 'text'}
      >
        <input
          id={`${prefix}-output`}
          type="number"
          step="any"
          min="0"
          inputMode="decimal"
          value={values.outputPrice}
          onChange={(event) => set({ outputPrice: event.target.value })}
        />
      </Field>
      {kind === 'image' && (
        <>
          <Field
            label="画像出力単価"
            htmlFor={`${prefix}-imageout`}
            hint="100万トークンあたりUSD。画像料金の大半はここです。設定すると1枚あたりの固定単価より優先されます。"
          >
            <input
              id={`${prefix}-imageout`}
              type="number"
              step="any"
              min="0"
              inputMode="decimal"
              value={values.imageOutputPrice}
              onChange={(event) => set({ imageOutputPrice: event.target.value })}
            />
          </Field>
          <Field
            label="1枚あたりの固定単価"
            htmlFor={`${prefix}-each`}
            hint="USD。DALL·E のように枚数で課金するモデル用。トークン単価が未設定のときの保険にもなります。"
          >
            <input
              id={`${prefix}-each`}
              type="number"
              step="any"
              min="0"
              inputMode="decimal"
              value={values.imagePrice}
              onChange={(event) => set({ imagePrice: event.target.value })}
            />
          </Field>
        </>
      )}
    </>
  );

  const rows = (kind: 'text' | 'image') => data.items.filter((item) => item.kind === kind);
  const active = (kind: 'text' | 'image') => rows(kind).find((item) => item.is_active) ?? null;

  const table = (kind: 'text' | 'image') => {
    const list = rows(kind);
    if (list.length === 0) return <Empty>登録されたモデルがありません。</Empty>;
    return (
      <Table
        head={
          <>
            <th scope="col">モデル</th>
            <th scope="col">入力</th>
            <th scope="col">{kind === 'text' ? '出力' : 'テキスト出力'}</th>
            {kind === 'image' && <th scope="col">画像出力</th>}
            {kind === 'image' && <th scope="col">1枚固定</th>}
            <th scope="col" className="num">
              {kind === 'text' ? '記事1本の目安' : '画像1枚の目安'}
            </th>
            <th scope="col">状態</th>
            <th scope="col" />
          </>
        }
      >
        {list.map((model) => {
          const cost = estimate(model);
          return (
            <tr key={model.id} {...rowOpen(() => openEdit(model))}>
              <td>
                {model.label}
                <span className="muted small" style={{ display: 'block' }}>
                  {model.provider}／{model.model}
                  {!usable(model.provider) && (
                    <>
                      {' '}
                      <Pill tone="warn">キー未設定</Pill>
                    </>
                  )}
                </span>
              </td>
              <td className="num muted small">{dollars(model.input_micro_per_mtok)}</td>
              <td className="num muted small">{dollars(model.output_micro_per_mtok)}</td>
              {kind === 'image' && (
                <td className="num muted small">{dollars(model.image_output_micro_per_mtok)}</td>
              )}
              {kind === 'image' && (
                <td className="num muted small">{dollars(model.image_micro_each)}</td>
              )}
              {/*
                The figure the rates are for. Three rates per image is not
                something anybody should have to multiply out to answer "what
                does a picture cost".
              */}
              {/*
                The figure only. The assumption behind it is stated once above
                the table - repeating it on every row is thirty characters of
                identical text between the reader and the numbers they came for.
              */}
              <td className="num">
                {cost ? <strong>{money(cost.micro)}</strong> : <Pill tone="danger">価格未設定</Pill>}
              </td>
              <td>
                {model.is_active ? <Pill tone="ok">使用中</Pill> : <Pill tone="muted">待機</Pill>}
              </td>
              <td>
                <div className="rowactions">
                  {!model.is_active && (
                    <button
                      className="btn btn--sm"
                      type="button"
                      disabled={busy !== null}
                      onClick={() => void activate(model)}
                    >
                      {busy === model.id ? '切替中…' : '使用する'}
                    </button>
                  )}
                  <button
                    className="btn btn--ghost btn--sm"
                    type="button"
                    onClick={() => openEdit(model)}
                  >
                    編集
                  </button>
                  {!model.is_active && (
                    <button
                      className="btn btn--ghost btn--sm"
                      type="button"
                      onClick={() => setRemoving(model)}
                    >
                      削除
                    </button>
                  )}
                </div>
              </td>
            </tr>
          );
        })}
      </Table>
    );
  };

  return (
    <>
      <Card
        title="AIモデル"
        caption="全クリニックの生成に使われるモデルと、その単価です。"
        actions={
          <div className="chips">
            <InfoButton title="モデルと単価について">
              <p>
                ここで選んだモデルが、<strong>次の生成から</strong>全クリニックに適用されます。
                APIキーは環境変数で設定します（画面には出しません）。
              </p>

              <h3 className="infobody__title">単価の考え方</h3>
              <Checklist
                items={[
                  '文章は「入力トークン」と「出力トークン」の2つの単価で課金される',
                  '画像は最大4つ：入力・テキスト出力・画像出力・1枚あたりの固定単価',
                  '画像出力の単価を設定すると、実際に使われたトークン数で計算する（固定単価より優先）',
                  '固定単価は、枚数で課金するモデルと、使用量が返らなかったときの保険として残す',
                  '単価が1つも設定されていないモデルは、請求額が0になり誤りが見えなくなる',
                ]}
              />

              <h3 className="infobody__title">「目安」の前提</h3>
              <p className="small">
                記事1本＝入力{TYPICAL.articleInputTokens.toLocaleString('ja-JP')}・出力
                {TYPICAL.articleOutputTokens.toLocaleString('ja-JP')}トークン、 画像1枚＝1K画像
                {TYPICAL.imageOutputTokens.toLocaleString('ja-JP')}トークン＋プロンプト
                {TYPICAL.imagePromptTokens.toLocaleString('ja-JP')}トークンで計算しています。
                実際の請求は、生成のたびに提供元が返した実使用量で計算されます。
                2K画像を指定すれば画像出力トークンは約4倍になります。
              </p>
            </InfoButton>
            <button
              className="btn btn--sm"
              type="button"
              onClick={() => {
                setForm(blank);
                setAdding(true);
              }}
            >
              ＋ モデルを追加
            </button>
          </div>
        }
      >
        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}

        {/* What is running right now, and what one unit of it costs. */}
        <div className="grid grid--2">
          {(['text', 'image'] as const).map((kind) => {
            const current = active(kind);
            const cost = current ? estimate(current) : null;
            return (
              <div className="stat" key={kind}>
                <div className="stat__label">{KIND_LABEL[kind]}</div>
                <div className="stat__value stat__value--sm">{current?.label ?? '未選択'}</div>
                <div className="small" style={{ opacity: 0.85 }}>
                  {current
                    ? usable(current.provider)
                      ? `${current.provider}${cost ? `・${kind === 'text' ? '記事1本' : '画像1枚'} ${money(cost.micro)}` : '・価格未設定'}`
                      : `${current.provider}（キー未設定のためスタブ動作）`
                    : '既定のモデルで動作します'}
                </div>
              </div>
            );
          })}
        </div>

        <p className="form__section">文章生成</p>
        <p className="muted small" style={{ marginTop: 0 }}>
          単価は100万トークンあたりUSD。目安は 入力
          {TYPICAL.articleInputTokens.toLocaleString('ja-JP')}・出力
          {TYPICAL.articleOutputTokens.toLocaleString('ja-JP')}トークンの記事1本で計算しています。
        </p>
        {table('text')}

        <p className="form__section">画像生成</p>
        <p className="muted small" style={{ marginTop: 0 }}>
          画像は最大4つの単価で課金されます。画像出力の単価があるときは実使用トークンで計算し、
          ないときは1枚あたりの固定単価を使います。目安は 1K画像（画像出力
          {TYPICAL.imageOutputTokens.toLocaleString('ja-JP')}トークン＋プロンプト
          {TYPICAL.imagePromptTokens.toLocaleString('ja-JP')}トークン）で計算しています。
        </p>
        {table('image')}
      </Card>

      {/* Correcting a price without waiting for a release: a wrong one bills everybody. */}
      {editing && (
        <ModalForm
          open
          onClose={() => setEditing(null)}
          title="モデルを編集"
          caption={`${editing.provider}／${editing.model}：単価は次の生成から適用されます`}
          submitLabel="保存する"
          wide
          onSubmit={async () => {
            const values = editForm;
            await patch(`/admin/ai-models/${editing.id}`, {
              label: values.label.trim(),
              notes: values.notes.trim(),
              ...(micro(values.inputPrice) === undefined
                ? {}
                : { inputMicroPerMtok: micro(values.inputPrice) }),
              ...(micro(values.outputPrice) === undefined
                ? {}
                : { outputMicroPerMtok: micro(values.outputPrice) }),
              ...(editing.kind === 'image' && micro(values.imageOutputPrice) !== undefined
                ? { imageOutputMicroPerMtok: micro(values.imageOutputPrice) }
                : {}),
              ...(editing.kind === 'image' && micro(values.imagePrice) !== undefined
                ? { imageMicroEach: micro(values.imagePrice) }
                : {}),
            });
            setEditing(null);
            onChanged();
          }}
        >
          <Field label="表示名" htmlFor="edit-label" required>
            <input
              id="edit-label"
              value={editForm.label}
              maxLength={120}
              required
              onChange={(event) => setEditForm({ ...editForm, label: event.target.value })}
            />
          </Field>
          <Field label="メモ" htmlFor="edit-notes">
            <input
              id="edit-notes"
              value={editForm.notes}
              maxLength={500}
              onChange={(event) => setEditForm({ ...editForm, notes: event.target.value })}
            />
          </Field>
          {priceFields(
            editing.kind,
            editForm,
            (next) => setEditForm({ ...editForm, ...next }),
            'edit',
          )}
        </ModalForm>
      )}

      {removing && (
        <ModalForm
          open
          onClose={() => setRemoving(null)}
          title="このモデルを削除しますか"
          caption={`${removing.label}：使用中のモデルは削除できません。`}
          submitLabel="削除する"
          danger
          onSubmit={async () => {
            await del(`/admin/ai-models/${removing.id}`);
            setRemoving(null);
            onChanged();
          }}
        >
          <p className="field--full" style={{ margin: 0 }}>
            過去の生成の記録は残ります。この一覧から選べなくなるだけです。
          </p>
        </ModalForm>
      )}

      <ModalForm
        open={adding}
        onClose={() => setAdding(false)}
        title="モデルを追加"
        caption="追加しただけでは使われません。「使用する」で切り替えます。"
        submitLabel="追加する"
        wide
        onSubmit={async () => {
          const kind = form.kind as 'text' | 'image';
          if (kind === 'image' && !form.imageOutputPrice.trim() && !form.imagePrice.trim()) {
            throw new Error('画像出力単価か、1枚あたりの固定単価のどちらかを入力してください');
          }
          await post('/admin/ai-models', {
            kind,
            provider: form.provider,
            model: form.model.trim(),
            label: form.label.trim(),
            ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
            ...(micro(form.inputPrice) === undefined
              ? {}
              : { inputMicroPerMtok: micro(form.inputPrice) }),
            ...(micro(form.outputPrice) === undefined
              ? {}
              : { outputMicroPerMtok: micro(form.outputPrice) }),
            ...(kind === 'image' && micro(form.imageOutputPrice) !== undefined
              ? { imageOutputMicroPerMtok: micro(form.imageOutputPrice) }
              : {}),
            ...(kind === 'image' && micro(form.imagePrice) !== undefined
              ? { imageMicroEach: micro(form.imagePrice) }
              : {}),
          });
          setForm(blank);
          setAdding(false);
          onChanged();
        }}
      >
        <Field label="種別" htmlFor="new-kind">
          <Select
            id="new-kind"
            value={form.kind}
            onChange={(kind) => setForm({ ...form, kind })}
            options={[
              { value: 'text', label: '文章生成' },
              { value: 'image', label: '画像生成' },
            ]}
          />
        </Field>
        <Field label="提供元" htmlFor="new-provider">
          <Select
            id="new-provider"
            value={form.provider}
            onChange={(provider) => setForm({ ...form, provider })}
            options={[
              { value: 'gemini', label: 'Google Gemini' },
              { value: 'openai', label: 'OpenAI' },
              { value: 'stub', label: 'スタブ（テスト用・課金なし）' },
            ]}
          />
        </Field>
        <Field label="モデルID" htmlFor="new-model" required hint="提供元が指定する識別子。">
          <input
            id="new-model"
            value={form.model}
            maxLength={100}
            required
            placeholder="gemini-3.5-flash"
            onChange={(event) => setForm({ ...form, model: event.target.value })}
          />
        </Field>
        <Field label="表示名" htmlFor="new-label" required>
          <input
            id="new-label"
            value={form.label}
            maxLength={120}
            required
            placeholder="Gemini 3.5 Flash"
            onChange={(event) => setForm({ ...form, label: event.target.value })}
          />
        </Field>
        {priceFields(
          form.kind as 'text' | 'image',
          form,
          (next) => setForm({ ...form, ...next }),
          'new',
        )}
      </ModalForm>
    </>
  );
}
